<template>
  <div class="live-streaming-container" v-if="eventLoaded">
    <TopBar>
      <div
        class="d-flex flex-row align-items-center justify-content-between h-100"
      >
        <div class="d-flex align-items-center pl-3 h-100">
          <span class="c-charcoal d-none d-sm-inline-block">{{
            event.event_name
          }}</span>
          <span class="pl-3" v-if="!displaySetup">{{ sessionDuration }}</span>
        </div>
        <div class="d-flex align-items-center h-100" v-if="!displaySetup">
          <button class="btn top-right-btn mr-2" @click="toggleSetup">
            <i class="fal fa-wrench"></i>
          </button>
          <button
            v-if="!chatDisabled"
            class="btn top-right-btn"
            :class="{ 'chat-opened': !isChatClosed }"
            @click="toggleChat"
          >
            <i class="fal fa-comments-alt"></i>
            <span v-if="newMessagesCount > 0" class="msg-count">{{
              newMessagesCount
            }}</span>
          </button>
        </div>
      </div>
    </TopBar>
    <div v-if="event.is_free_event" class="text-center">
      {{ $t('EVENTS.TEXT.BROWSER_STREAMING_DISABLED_FOR_FREE_EVENTS') }}
    </div>
    <div v-else>
      <div
        v-if="displaySetup"
        class="container h-100 d-flex align-items-center justify-content-center"
      >
        <OpenTokPreStreamSetup
          class="bg-white p-3"
          :buttonText="$t('EVENTS.BUTTON.START_STREAMING')"
          @close="closeSetup"
        />
      </div>
      <div v-else class="container-fluid stream">
        <div class="row h-100">
          <OpenTokSession
            class="col-sm-12 m-0 p-0 h-100 d-flex flex-row live-session"
            :class="{ 'chat-expanded': !isChatClosed }"
            :sessionId="event.streaming_session_id"
            :token="event.streaming_token"
            :name="event.event_name"
            :membersList="nonOwnerMembers"
            :isStartStopSessionInProgress="isStartStopSessionInProgress"
            :isRecording="isRecording"
            :isSetupClosed="isSetupClosed"
            :logoUrl="studioLogo"
            @toggleRecording="toggleRecording"
            @stop="stopStreamingSession(true)"
          />
          <!-- Stays mounted (and closed) when chat is disabled: the attendee list comes from its channel members -->
          <HMChat
            v-if="user"
            class="chat-container col-sm-12 m-0 p-0 h-100"
            :class="{ closed: isChatClosed }"
            :userId="`${user.user_id}`"
            :isTeacher="true"
            :userName="user.user_full_name"
            :userImage="user.avatar"
            :userToken="event.stream_chat_token"
            :channelId="event.chat_channel_id"
            :channelName="event.event_name"
            @membersUpdated="chatMembersUpdated"
            @newMessage="chatNewMessage"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import OpenTokSession from './OpenTokSession';
import { mapGetters } from 'vuex';
import { errorToMessage } from '@/core/utils/errors';
import sleep from '@/web/utils/sleep';
import HMChat from '@/web/components/chat/HMChat';
import TopBar from '@/web/components/navigation/TopBar';
import OpenTokPreStreamSetup from '@/web/views/admin/events/streaming/OpenTokPreStreamSetup';

export default {
  name: 'OpenTokLiveStream',
  components: {
    OpenTokSession,
    HMChat,
    TopBar,
    OpenTokPreStreamSetup,
  },
  computed: {
    ...mapGetters({
      event: 'event/event',
      user: 'user/user',
      teacher: 'user/teacher',
      streamingProps: 'setting/streamingProps',
    }),
    nonOwnerMembers() {
      return this.membersList.filter(member => member.role !== 'owner');
    },
    chatDisabled() {
      return this.teacher?.chat_disabled;
    },
    studioLogo() {
      return (
        this.teacher?.logo?.storage_url ??
        this.teacher?.logo?.embedded_image_url
      );
    },
  },
  data() {
    return {
      displaySetup: true,
      isChatClosed: true,
      isSetupClosed: true,
      sessionStartTime: moment(),
      sessionDuration: '',
      stopwatchInterval: null,
      membersList: [],
      newMessagesCount: 0,
      eventLoaded: false,
      isStartStopSessionInProgress: false,
      isRecording: false,
      sessionStopped: false,
    };
  },
  methods: {
    closeSetup() {
      this.displaySetup = false;
      this.sessionStartTime = moment();
      this.$rollbar.info(
        `TokBox starting live stream - ${this.teacher.studio_name}`,
        {
          ...this.streamingProps,
          studioName: this.teacher.studio_name,
          studioUrl: this.teacher.studio_url,
          eventName: this.event.event_name,
          eventId: this.event.id,
        }
      );
    },
    eventDuration() {
      const duration = moment.duration(moment().diff(this.sessionStartTime));
      this.sessionDuration = moment()
        .hour(duration.hours())
        .minute(duration.minutes())
        .second(duration.seconds())
        .format('HH:mm:ss');
    },
    async stopStreamingSession(goBack) {
      if (this.sessionStopped) return; // prevent session being stopped again on route change
      try {
        await this.$store.dispatch('event/stopStreamingSession', this.event.id);
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t('EVENTS.TEXT.STREAMING_STOPPED'),
        });
        this.sessionStopped = true;
        if (goBack) this.$router.go(-1);
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to end live stream',
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      }
    },
    async stopRecording() {
      try {
        await this.$store.dispatch('event/stopSessionRecording', this.event.id);
        this.isRecording = false;
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to stop recording',
          text: errorToMessage(error.response, `Request failed: ${error}`),
        });
      } finally {
        this.isStartStopSessionInProgress = false;
      }
    },
    async toggleRecording() {
      this.isStartStopSessionInProgress = true;
      if (!this.isRecording) {
        try {
          await this.$store.dispatch(
            'event/startSessionRecording',
            this.event.id
          );
          this.isRecording = true;
        } catch (error) {
          this.$notify({
            group: 'app',
            type: 'error',
            title: 'Failed to start recording',
            text: errorToMessage(error.response, `Request failed: ${error}`),
          });
        } finally {
          this.isStartStopSessionInProgress = false;
        }
      } else {
        await this.stopRecording();
      }
    },
    chatMembersUpdated(membersList) {
      this.membersList = membersList;
    },
    chatNewMessage() {
      if (this.isChatClosed) {
        this.newMessagesCount += 1;
      }
    },
    toggleChat() {
      this.isChatClosed = !this.isChatClosed;
      if (!this.isChatClosed) {
        this.newMessagesCount = 0;
      }
    },
    toggleSetup() {
      this.isSetupClosed = !this.isSetupClosed;
    },
  },
  async mounted() {
    sleep.prevent();
    try {
      await this.$store.dispatch(
        'event/getEventDetails',
        this.$route.params.id
      );
      if (!this.event.streaming_session_id) {
        await this.$store.dispatch(
          'event/startStreamingSession',
          this.$route.params.id
        );
      }
      this.eventLoaded = true;
    } catch (error) {
      console.error(error);
    }
    window.onbeforeunload = () => {
      // close the session when user close the tab or browser
      sleep.allow();
      this.stopStreamingSession(false);
    };
    window.onunload = () => {
      sleep.allow();
      this.stopStreamingSession(false);
    };
    this.stopwatchInterval = setInterval(this.eventDuration, 1000);
    window.Intercom('update', { hide_default_launcher: true });
    window.Userback && window.Userback.hide();
  },
  beforeDestroy() {
    window.Intercom('update', { hide_default_launcher: false });
    clearInterval(this.stopwatchInterval);
    sleep.allow();
    this.stopStreamingSession(false);
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    window.onbeforeunload = () => {};
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    window.onunload = () => {};

    window.Userback && window.Userback.show();
  },
};
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.live-streaming-container {
  background-color: $light-gray;
  min-height: 100vh;
  height: 100%;
  max-height: 100%;

  .stream {
    max-height: unquote('calc(100vh - #{$streaming-topbar-height})');
    height: unquote('calc(100vh - #{$streaming-topbar-height})');
  }

  .live-session {
    width: 100%;
    max-width: 100%;

    &.chat-expanded {
      width: 66%;
      max-width: 66%;

      @media screen and (max-width: 760px) {
        width: 100%;
        max-width: 100%;
      }
    }
  }

  .top-right-btn {
    background-color: $white;
    color: $dark-gray;
    border-radius: 0;
    border-top: none;
    border-right: none;
    border-bottom: none;
    border-left: 1px solid $light-gray;
    padding: 0.5rem 1.5rem;

    &:hover {
      background-color: $medium-gray;
    }

    &.chat-opened {
      background-color: $charcoal;
      color: $white;
    }

    .msg-count {
      border-radius: 50%;
      width: 1rem;
      height: 1rem;
      background-color: $coral;
      color: white;
      font-size: 0.5rem;
      padding: 4px;
      position: absolute;
      top: 0.5rem;
      right: 1rem;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }

  .chat-container {
    position: relative;
    right: 0;
    width: 34%;
    max-width: 34%;
    height: unquote('calc(100vh - #{$streaming-topbar-height})');
    max-height: unquote('calc(100vh - #{$streaming-topbar-height})');
    transition: width ease-in-out 0.3s;

    @media screen and (max-width: 1200px) {
      width: 34%;
      max-width: 34%;
    }

    @media screen and (max-width: 992px) {
      width: 34%;
      max-width: 34%;
    }

    @media screen and (max-width: 760px) {
      width: 100%;
      max-width: 100%;
    }

    &.open {
    }

    &.closed {
      position: fixed;
      max-height: 0;
      width: 0;
      visibility: hidden;
    }
  }
}
</style>
